import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { trim } from 'lodash';
import { LocalizationService } from '../../../localization/localization.service';
import { IFormatAsNumberOptions } from '../../../shared/directives/format-as-number/format-as-number.directive';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';

@Component({
  selector: 'app-number-field',
  templateUrl: './number-field.component.html',
  styleUrls: ['./number-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberFieldComponent),
      multi: true,
    },
  ],
})
export class NumberFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit {
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() e2eId: string;
  @Input() placeholder: string;
  @Input() locale: string;
  @Input() decimalDigits: number = 2;
  @Input() formControl: FormControl;

  @Output() onFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() onBlur: EventEmitter<void> = new EventEmitter<void>();

  formatAsNumberOptions: IFormatAsNumberOptions;
  initialValue: string;
  internalFormControl: AbstractControl;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _localizationService: LocalizationService
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnInit() {
    this.formatAsNumberOptions = {
      decimalDigits: this.decimalDigits,
      decimalSeparator: this._localizationService.getDecimalSeparator(
        this.locale
      ),
      locale: this.locale,
      onBlur: this.convertToNumber.bind(this),
    };
    this.fieldControl = this.formControl;

    super.ngOnInit();
  }

  writeValue(value: any) {
    if (!this.internalFormControl) {
      this.initFormControl(value);
    } else {
      this.internalFormControl.setValue(value, { emitEvent: false });
    }
  }

  initFormControl(value: any) {
    this.internalFormControl = new FormControl(value || 0);
    this.initialValue = this.convertToNumber(value);

    this.internalFormControl.valueChanges.subscribe((currentValue) => {
      const formattedValue = currentValue
        ? currentValue.replace(this.formatAsNumberOptions.decimalSeparator, '.')
        : 0;

      this.propagateChange(parseFloat(formattedValue));
    });
  }

  convertToNumber(value: any): string {
    if (value) {
      const decimalSeparator: string = this.formatAsNumberOptions
        .decimalSeparator;
      const localizedValue: string = value
        .toString()
        .replace('.', decimalSeparator);

      return trim(localizedValue, decimalSeparator);
    } else {
      return value;
    }
  }
}
